import React, { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Chip } from '../ui';
import { PICK_CHIP, clampActive, levelLabel, listboxKey, pickerChoice } from './allocation';
import type { PickOption } from './allocation';
import type { Allocation } from './types';

interface Props {
  id: string;
  // The visible "Joins last 10 min" label; the trigger's name is that label plus its value.
  labelId: string;
  allocation: Allocation | null;
  options: PickOption[];
  disabled?: boolean;
  busy?: boolean;
  // null clears the allocation.
  onPick: (option: PickOption | null) => void;
}

const levelDot = (level: string): string =>
  level === 'L3' ? 'var(--state-warning)' : 'var(--state-info)';

/*
 * A select-only combobox: the trigger opens a listbox that keeps focus and moves an
 * active option (aria-activedescendant). It opens in place, no portal, so the list
 * pushes the row down at every width. Row 0 is "Not allocated".
 */
function RepPicker({ id, labelId, allocation, options, disabled, busy, onPick }: Props) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const current = allocation?.ops_uuid ? allocation : null;
  const listId = `${id}-list`;
  const optionId = (i: number) => `${id}-opt-${i}`;
  const count = options.length + 1;

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    // Start on the current pick, so Enter keeps it.
    setActive(options.findIndex((o) => o.selected) + 1);
    listRef.current?.focus();
    return () => document.removeEventListener('mousedown', onDown);
    // Once per open, not on every poll refresh of the options.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // A poll can reorder or shrink the options while the list is open: keep the same rep active.
  const optionKey = options.map((o) => o.rep.ops_uuid).join(',');
  // Same key means same order, so the list from the last key change maps indexes correctly.
  const prevOptionsRef = useRef(options);
  useEffect(() => {
    const prev = prevOptionsRef.current;
    prevOptionsRef.current = options;
    setActive((a) => clampActive(a, a > 0 ? prev[a - 1]?.rep.ops_uuid || null : null, options));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionKey]);

  useEffect(() => {
    if (open) document.getElementById(optionId(active))?.scrollIntoView?.({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, active]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const choose = (i: number) => {
    const choice = pickerChoice(i, options);
    if (!choice) return;
    close();
    onPick(choice.kind === 'clear' ? null : choice.option);
  };

  const onListKey = (e: ReactKeyboardEvent<HTMLUListElement>) => {
    const action = listboxKey(e.key, active, count);
    if (!action) return;
    e.preventDefault();
    if (action.kind === 'move') setActive(action.index);
    else if (action.kind === 'choose') choose(active);
    else close();
  };

  const rowStyle = (i: number, selected: boolean): React.CSSProperties => ({
    minHeight: 44,
    borderTop: i === 0 ? 0 : '1px solid var(--border-subtle)',
    background: selected ? 'var(--brand-primary-soft)' : 'var(--surface-card)',
    color: 'var(--text-primary)',
    cursor: 'pointer',
    // The active option gets an inset ring; selection keeps its tint.
    boxShadow: open && i === active ? 'inset 0 0 0 2px var(--brand-primary)' : 'none'
  });

  let triggerText = 'Not allocated';
  if (current)
    triggerText = `${current.name}${current.level ? ` · ${levelLabel(current.level)}` : ''}`;

  return (
    <div ref={rootRef} className="flex flex-col gap-1.5">
      <button
        ref={triggerRef}
        id={id}
        type="button"
        aria-labelledby={`${labelId} ${id}-value`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        disabled={disabled || busy}
        // Open: keep focus in the list on mousedown so the click closes it instead of blur-close-reopen.
        onMouseDown={(e) => {
          if (open) e.preventDefault();
        }}
        onClick={() => (open ? close() : setOpen(true))}
        className="flex w-full items-center gap-2 text-left text-[13px] max-sm:!h-11"
        style={{
          margin: 0,
          height: 34,
          padding: '0 10px',
          borderRadius: 8,
          border: `1px solid ${current ? 'var(--input-border)' : 'var(--brand-primary)'}`,
          background: 'var(--surface-card)',
          color: 'var(--text-primary)',
          cursor: disabled || busy ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1
        }}
      >
        <span id={`${id}-value`} className="min-w-0 flex-1 truncate">
          {busy ? 'Saving…' : triggerText}
        </span>
        <span aria-hidden="true" className="text-[10px] text-content-muted">
          ▾
        </span>
      </button>
      {open ? (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-label="Choose a rep"
          aria-activedescendant={optionId(active)}
          onKeyDown={onListKey}
          onBlur={(e) => {
            // Tab or a click elsewhere: close and let focus go where it went.
            if (!listRef.current?.contains(e.relatedTarget as Node | null)) setOpen(false);
          }}
          className="m-0 list-none overflow-hidden rounded-[10px] p-0 outline-none"
          style={{ border: '1px solid var(--brand-primary)', boxShadow: 'var(--shadow-modal)' }}
        >
          {/* Mouse-only handlers: keyboard selection runs through the listbox's onKeyDown. */}
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
          <li
            id={optionId(0)}
            role="option"
            aria-selected={!current}
            onClick={() => choose(0)}
            onMouseEnter={() => setActive(0)}
            className="flex list-none items-center px-3 py-2 text-[13px] text-content-secondary"
            style={rowStyle(0, !current)}
          >
            Not allocated
          </li>
          {options.map((o, k) => {
            const i = k + 1;
            const chip = PICK_CHIP[o.chip];
            const muted = o.chip === 'off_shift';
            return (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={o.rep.ops_uuid}
                id={optionId(i)}
                role="option"
                aria-selected={o.selected}
                onClick={() => choose(i)}
                onMouseEnter={() => setActive(i)}
                className="flex list-none items-center gap-2.5 px-3 py-2"
                style={rowStyle(i, o.selected)}
              >
                <span
                  aria-hidden="true"
                  className="h-2 w-2 shrink-0 rounded-sm"
                  style={{ background: levelDot(o.rep.level) }}
                />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span
                    className={`text-[13px] font-semibold ${
                      muted ? 'text-content-muted' : 'text-content-primary'
                    }`}
                  >
                    {o.rep.name}{' '}
                    <span className="font-normal text-content-muted">
                      · {levelLabel(o.rep.level)}
                    </span>
                  </span>
                  <span className="text-xs text-content-secondary">{o.line}</span>
                </span>
                <Chip tone={chip.tone}>{chip.label}</Chip>
              </li>
            );
          })}
        </ul>
      ) : null}
      {open && options.length === 0 ? (
        <span className="text-xs text-content-muted">No L2 or L3 reps on the roster</span>
      ) : null}
    </div>
  );
}

export default RepPicker;
